import React, { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../../common/hooks/hooks";
import { RootState } from "../../../../../../app/store";
import { setCurrentStep } from "../../../checkoutSlice";
import { useUpdateCustomer } from "../../../../cart/hooks/useUpdateCustomer";
import { useGetCheckoutDataQuery } from "../../../api/checkoutApi";
import { useGetCartQuery } from "../../../../cart/api/cartApi";
import {
  createShippingSchema,
  ShippingFormData,
} from "../../../helpers/checkoutValidation";
import { useUnsavedChangesWarning } from "../../../../../../common/hooks/useUnsavedChangesWarning";
import CheckoutDetailsForm from "../../common/CheckoutDetailsForm/CheckoutDetailsForm";
import CheckoutStepLayout from "../../common/CheckoutStepLayout";
import ShippingMethods from "./ShippingMethods";
import UseBillingForShippingToggle from "../../common/UseBillingForShippingToggle";

/**
 * Shipping Step Component
 * Flow: Prev = Submit & Back to Billing. Next = Submit & Forward to Payment.
 * Features: Live shipping rate updates based on address changes.
 */
const ShippingStep: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // --- Direction Tracking ---
  // Tracks which button was clicked to trigger the submit
  const navigationDirection = useRef<"prev" | "next">("next");

  // --- Global State ---
  const useBillingForShipping = useAppSelector(
    (state: RootState) => state.checkout.useBillingForShipping,
  );

  const { data: checkoutData } = useGetCheckoutDataQuery();
  const { data: cartData } = useGetCartQuery();

  const { handleUpdateCustomer, isUpdatingCustomer } = useUpdateCustomer();

  // --- Form Initialization ---
  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    getValues,
    formState: { errors, isDirty, isSubmitting },
    reset,
  } = useForm<ShippingFormData>({
    resolver: zodResolver(createShippingSchema(t)),
    mode: "onSubmit",
    defaultValues: {
      ...(!useBillingForShipping
        ? checkoutData?.shippingAddress
        : checkoutData?.billingAddress),
      country: checkoutData?.shippingAddress?.country || "",
    },
  });

  useUnsavedChangesWarning(isDirty, isSubmitting);

  // --- Live Update Logic ---
  // We watch specific fields that affect shipping rates to trigger updates without submitting the full form.
  const watchedAddress = watch(["country", "state", "city", "postcode"]);
  const lastSyncedPayload = useRef<string>("");

  useEffect(() => {
    // If using billing address, this form is hidden/disabled, so we don't trigger updates here.
    if (useBillingForShipping) return;

    const [country] = watchedAddress;

    // We need at least a country to calculate rates
    if (!country) return;

    // Debounce the update to avoid API spam while typing
    const timer = setTimeout(() => {
      const currentValues = getValues();
      const serverShipping = checkoutData?.shippingAddress;

      // Check if values actually changed compared to what the server already has
      const isDifferent =
        currentValues.country !== serverShipping?.country ||
        currentValues.state !== serverShipping?.state ||
        currentValues.city !== serverShipping?.city ||
        currentValues.postcode !== serverShipping?.postcode;

      if (!isDifferent) return;

      // Prepare payload for background update
      const payload = {
        shipping_address: {
          country: currentValues.country,
          state: currentValues.state,
          city: currentValues.city,
          postcode: currentValues.postcode,
          // We include other fields to ensure validation passes on the backend if needed
          address_1: currentValues.address1,
          address_2: currentValues.address2,
        },
      };

      const payloadString = JSON.stringify(payload);

      // Prevent duplicate requests if payload hasn't changed
      if (payloadString !== lastSyncedPayload.current) {
        lastSyncedPayload.current = payloadString;
        // Fire update silently (catch errors to prevent UI disruption)
        handleUpdateCustomer(payload).catch((err) =>
          console.debug("Background shipping update skipped", err),
        );
      }
    }, 1000); // 1s delay

    return () => clearTimeout(timer);
  }, [
    watchedAddress[0], // country
    watchedAddress[1], // state
    watchedAddress[2], // city
    watchedAddress[3], // postcode
    useBillingForShipping,
    handleUpdateCustomer,
    checkoutData,
    getValues,
  ]);

  // --- Server State Sync ---
  // We use a ref to track the previous toggle state.
  const prevUseBillingForShipping = useRef(useBillingForShipping);

  useEffect(() => {
    if (checkoutData) {
      const hasModeChanged =
        prevUseBillingForShipping.current !== useBillingForShipping;

      // If mode changed OR form is pristine, update the fields.
      if (hasModeChanged || !isDirty) {
        const addressData = !useBillingForShipping
          ? checkoutData.shippingAddress
          : checkoutData.billingAddress;

        reset({
          ...addressData,
          country: addressData.country || "",
        });
      }
    }
    prevUseBillingForShipping.current = useBillingForShipping;
  }, [checkoutData, useBillingForShipping, reset, isDirty]);

  // --- Button Click Handlers (Set Intent) ---
  const onPrevClick = () => {
    navigationDirection.current = "prev";
    // No dispatch/navigation here, the form submit handles it
  };

  const onNextClick = () => {
    navigationDirection.current = "next";
  };

  // --- Submission Handler ---
  const onSubmit = async (data: ShippingFormData) => {
    // 1. Validate Shipping Method Selection
    const hasSelectedRate = cartData?.shippingRates?.some(
      (rate) => rate.selected,
    );

    if (!hasSelectedRate) {
      toast.error(
        t(
          "feedback.cart.shippingRate.selectShippingRateError",
          "Please select a shipping method",
        ),
      );
      return;
    }

    // 2. Prepare Payload
    let payload: any;

    if (!useBillingForShipping) {
      // CASE A: Custom Shipping Address
      payload = {
        shipping_address: {
          first_name: data.firstName,
          last_name: data.lastName,
          company: data.company,
          country: data.country,
          address_1: data.address1,
          address_2: data.address2,
          city: data.city,
          state: data.state,
          postcode: data.postcode,
          phone: data.phone,
        },
      };
    } else {
      // CASE B: Use Billing Address (Sync server state)
      const billing = checkoutData?.billingAddress;
      payload = {
        shipping_address: {
          first_name: billing?.firstName,
          last_name: billing?.lastName,
          company: billing?.company,
          country: billing?.country,
          address_1: billing?.address1,
          address_2: billing?.address2,
          city: billing?.city,
          state: billing?.state,
          postcode: billing?.postcode,
          phone: billing?.phone,
        },
      };
    }

    // 3. Execute Update (Save to Server)
    const result = await handleUpdateCustomer(payload);

    if (result.success) {
      reset(data); // Mark clean

      // 4. Navigate based on Intent
      if (navigationDirection.current === "prev") {
        dispatch(setCurrentStep("billing"));
      } else {
        dispatch(setCurrentStep("payment"));
      }
    }
  };

  const onError = () => {
    if (!useBillingForShipping) {
      toast.error(
        t(
          "feedback.cart.customer.missingFieldBackend",
          "Please fill in all required fields.",
        ),
      );
    } else {
      // Should ideally not happen if fields are disabled/hidden
      toast.error(
        t(
          "checkoutPage.billingInvalid",
          "Billing address details are incomplete. Please go back and check them.",
        ),
      );
    }
  };

  // If the cart doesn't need shipping (handled by MiddleStep), don't render to avoid flash
  if (cartData && !cartData.needsShipping) return null;

  return (
    <CheckoutStepLayout
      title={t("shippingStep.stepTitle", "Shipping Details")}
      onSubmit={handleSubmit(onSubmit, onError)}
      isProcessing={isUpdatingCustomer}
      nextButtonText={t("checkoutForm.actions.continue", "Continue to Payment")}
      isNextDisabled={false}
      // Configuration for "Submit on Prev" flow
      showPrevButton={true}
      prevButtonType="submit"
      onPrevious={onPrevClick} // Sets intent to 'prev'
      showNextButton={true}
      nextButtonType="submit"
      onNext={onNextClick} // Sets intent to 'next'
    >
      <UseBillingForShippingToggle />

      <div className="checkout-step__section">
        <CheckoutDetailsForm
          register={register}
          control={control}
          errors={errors}
          setValue={setValue}
          watch={watch}
          addressType="shipping"
          disabled={useBillingForShipping}
        />
      </div>

      <div className="checkout-step__section">
        <ShippingMethods />
      </div>
    </CheckoutStepLayout>
  );
};

export default ShippingStep;
